'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { LogOut, Loader2, Menu, X } from 'lucide-react';
import { api, getToken, clearToken } from '@/lib/api';
import { AdminPermissionProvider, createPermission } from '@/lib/admin-permission';
import { ADMIN_MENUS, menuForPath } from '@/lib/admin-menu';
// 알림 벨(인앱 알림) 비활성화 — 관리자 알림은 알림톡으로 발송. SSE/폴링(unread-count) 요청 폭주 제거.
// import { NotificationBell } from './NotificationBell';

interface Props {
  children: React.ReactNode;
}

// 중첩 AdminShell 감지용 컨텍스트.
// 지속 레이아웃(AdminLayoutGate)이 바깥 AdminShell을 1개만 렌더하며 provider=true를 내려주면,
// 각 page.tsx 내부에 남아있는 <AdminShell>은 passthrough로 동작 → 이동마다 재마운트/인증·배지·SSE 재실행 방지.
const AdminShellNestedContext = createContext(false);

export default function AdminShell({ children }: Props) {
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const nested = useContext(AdminShellNestedContext);
  const [ready, setReady] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [managerPerms, setManagerPerms] = useState<Record<string, { view?: boolean; edit?: boolean }>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingCounts, setPendingCounts] = useState<{ drawing: number; refund: number; report: number }>({
    drawing: 0,
    refund: 0,
    report: 0,
  });

  useEffect(() => {
    if (nested) return; // 중첩(page 내부) 인스턴스는 인증/렌더를 바깥 셸에 위임
    const token = getToken();
    if (!token) {
      router.replace('/login');
      return;
    }
    api
      .get('/auth/me')
      .then((res) => {
        const user = res.data?.data ?? res.data;
        if (!['MANAGER', 'ADMIN', 'SUPER_ADMIN'].includes(user?.role)) {
          clearToken();
          router.replace('/login');
          return;
        }
        setUserRole(user?.role || '');
        setManagerPerms(user?.managerPermissions || {});
        setReady(true);
      })
      .catch(() => {
        clearToken();
        router.replace('/login');
      });
  }, [router, nested]);

  // 30분 무활동 시 자동 로그아웃 (아이들 타임아웃) — 인증 완료 후에만 동작
  useEffect(() => {
    if (!ready) return;
    const ACTIVITY_KEY = 'aptgo-admin-last-activity';
    const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    let loggingOut = false;
    let lastMark = 0;

    const mark = () => {
      try {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      } catch {
        // 무시
      }
    };
    const onActivity = () => {
      const now = Date.now();
      if (now - lastMark < 1000) return;
      lastMark = now;
      mark();
    };
    const doLogout = async () => {
      if (loggingOut) return;
      loggingOut = true;
      try {
        await api.post('/auth/logout');
      } catch {
        // 서버 호출 실패해도 로컬 세션 정리
      }
      clearToken();
      router.replace('/login?reason=idle');
    };
    const checkIdle = () => {
      let last = 0;
      try {
        last = Number(localStorage.getItem(ACTIVITY_KEY) || '0');
      } catch {
        last = 0;
      }
      if (last && Date.now() - last >= IDLE_TIMEOUT_MS) void doLogout();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };

    mark();
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(checkIdle, 30 * 1000);

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [ready, router]);

  useEffect(() => {
    if (!ready) return;
    // 현재 경로의 메뉴 접근 권한(역할/섹션)을 단일 소스로 판정, 없으면 도면관리로 이동
    const menu = menuForPath(pathname);
    if (!menu) return;
    const p = createPermission(userRole, managerPerms);
    if (!p.canAccessMenu(menu)) {
      router.replace('/permit-images');
    }
  }, [pathname, ready, userRole, managerPerms, router]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // 신규 요청 건수 조회 (사이드바 배지).
  // - 관리자 진입/페이지 이동마다 갱신
  // - 각 페이지에서 상태 변경 시 window CustomEvent('admin:pending-refresh') 발송 → 즉시 재조회
  useEffect(() => {
    if (!ready) return;
    const fetchCounts = async () => {
      const totals = { drawing: 0, refund: 0, report: 0 };
      const results = await Promise.allSettled([
        api.get('/admin/drawing-requests?status=PENDING&limit=1'),
        api.get('/admin/refunds?status=PENDING_REVIEW&limit=1'),
        api.get('/admin/drawing-reports?status=RECEIVED&limit=1'),
      ]);
      const extractTotal = (r: PromiseSettledResult<unknown>): number => {
        if (r.status !== 'fulfilled') return 0;
        const val = (r.value as { data?: { pagination?: { total?: number }; total?: number } })?.data;
        return val?.pagination?.total ?? val?.total ?? 0;
      };
      totals.drawing = extractTotal(results[0]);
      totals.refund = extractTotal(results[1]);
      totals.report = extractTotal(results[2]);
      setPendingCounts(totals);
    };
    fetchCounts().catch(() => {});
    const onRefresh = () => {
      fetchCounts().catch(() => {});
    };
    window.addEventListener('admin:pending-refresh', onRefresh);
    return () => window.removeEventListener('admin:pending-refresh', onRefresh);
    // 최초 진입 1회 + admin:pending-refresh 이벤트로만 갱신(이동마다 재조회 방지)
  }, [ready]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // 서버 호출 실패해도 로컬 세션은 정리
    }
    clearToken();
    router.replace('/login');
  };

  // 중첩 인스턴스: 셸을 그리지 않고 콘텐츠만 통과(바깥 지속 셸이 실제 셸을 렌더)
  if (nested) return <>{children}</>;

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // 권한 판정 단일 소스 (메뉴 노출/접근)
  const perm = createPermission(userRole, managerPerms);

  const navItem = (href: string, label: string, Icon: React.ElementType, badgeCount?: number) => {
    const active = pathname === href || pathname.startsWith(href + '/');
    return (
      <Link
        key={href}
        href={href}
        prefetch={false}
        onClick={() => setDrawerOpen(false)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
          active
            ? 'bg-primary text-white font-semibold shadow-sm shadow-primary/20'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span>{label}</span>
        {badgeCount !== undefined && badgeCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  return (
    <AdminShellNestedContext.Provider value={true}>
    <div className="min-h-screen md:h-screen md:overflow-hidden md:flex">
      <header className="md:hidden sticky top-0 z-30 bg-white border-b flex items-center gap-2 px-3 h-14">
        <button
          onClick={() => setDrawerOpen(true)}
          className="text-gray-700 hover:bg-gray-100 rounded-lg p-2"
          aria-label="메뉴 열기"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link href="/" className="flex items-center gap-2 flex-1 min-w-0">
          <Image
            src="/logo.png"
            alt="AptGo"
            width={96}
            height={27}
            className="object-contain"
            priority
          />
          <span className="text-xs font-semibold text-gray-500 truncate">Admin</span>
        </Link>
        {/* <NotificationBell /> 알림톡으로 대체 — 인앱 알림 벨 비활성화 */}
        <button
          onClick={handleLogout}
          className="text-gray-500 hover:bg-gray-100 rounded-lg p-2"
          aria-label="로그아웃"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`
          bg-white border-r flex flex-col
          fixed top-0 left-0 h-full w-64 z-50 transform transition-transform duration-200
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
          md:static md:translate-x-0 md:w-56 md:h-auto md:min-h-screen md:z-auto
        `}
      >
        {/* 로고 영역: 우측 헤더와 동일한 높이(h-14)·하단 구분선으로 정렬 */}
        <div className="relative px-5 h-14 border-b flex items-center justify-center">
          <Image
            src="/logo.png"
            alt="AptGo"
            width={108}
            height={30}
            className="object-contain"
            priority
          />
          <button
            onClick={() => setDrawerOpen(false)}
            className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="메뉴 닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {ADMIN_MENUS.filter((m) => perm.canAccessMenu(m)).map((m) =>
            navItem(
              m.href,
              m.label,
              m.icon,
              m.badge === 'drawing'
                ? pendingCounts.drawing
                : m.badge === 'refund'
                  ? pendingCounts.refund
                  : m.badge === 'report'
                    ? pendingCounts.report
                    : undefined,
            ),
          )}
        </nav>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-5 py-3 border-t text-sm text-gray-600 hover:bg-gray-50"
        >
          <LogOut className="h-4 w-4" />
          로그아웃
        </button>
      </aside>

      <main className="flex-1 overflow-auto min-w-0 md:min-h-0">
        <header className="hidden md:flex sticky top-0 z-20 bg-white border-b items-center justify-end px-6 h-14 gap-2">
          <span className="text-xs font-semibold text-primary">Admin</span>
          {/* <NotificationBell /> 알림톡으로 대체 — 인앱 알림 벨 비활성화 */}
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg px-2.5 py-1.5"
            aria-label="로그아웃"
          >
            <LogOut className="h-4 w-4" />
            로그아웃
          </button>
        </header>
        <div className="max-w-6xl mx-auto px-4 py-5 sm:px-6 sm:py-8">
          <AdminPermissionProvider userRole={userRole} managerPerms={managerPerms}>
            {children}
          </AdminPermissionProvider>
        </div>
      </main>
    </div>
    </AdminShellNestedContext.Provider>
  );
}
